import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Gift, Heart, ArrowLeft, ChevronRight, CreditCard } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const STEPS = ['Recipient', 'Message', 'Payment'];

const GiftPurchase = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [form, setForm] = useState({
    buyer_name: '', buyer_email: '',
    recipient_name: '', recipient_email: '',
    message: '',
  });

  const cancelled = searchParams.get('cancelled');

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleCheckout = async () => {
    if (!form.buyer_name || !form.buyer_email || !form.recipient_name || !form.recipient_email) {
      toast({ title: 'Missing Info', description: 'Please fill in all required fields.', variant: 'destructive' });
      return;
    }
    setProcessing(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gift/create`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.checkout_url) window.location.href = data.checkout_url;
      else throw new Error(data.detail || 'Checkout failed');
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-amber-50 py-8 px-4" data-testid="gift-purchase-page">
      <div className="max-w-2xl mx-auto">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6 transition-colors" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" /><span className="font-medium">Back to Home</span>
        </button>

        <div className="text-center mb-8 fade-in">
          <div className="flex justify-center gap-3 mb-4">
            <Gift className="w-8 h-8 text-pink-400 float-animation" />
            <Heart className="w-8 h-8 text-purple-400 float-animation" style={{ animationDelay: '0.5s' }} />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-2">Give the Gift of a Lifetime</h1>
          <p className="text-gray-600">An AI-written autobiography for someone you love — just £25</p>
        </div>

        {/* Steps */}
        <div className="flex items-center justify-center gap-2 mb-10">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${i <= step ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-md' : 'bg-white/60 text-gray-400'}`}>
                {s}
              </div>
              {i < STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-gray-300" />}
            </React.Fragment>
          ))}
        </div>

        {cancelled && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-xl p-4 mb-6">Payment was cancelled. You can try again.</div>}

        {/* Step 0: Recipient Details */}
        {step === 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-8 fade-in" data-testid="step-recipient">
            <h3 className="text-xl font-bold text-gray-800 mb-6">Who is this gift for?</h3>
            <div className="space-y-4">
              <div><Label>Your Name *</Label><Input name="buyer_name" value={form.buyer_name} onChange={handleChange} placeholder="Your full name" className="mt-1" data-testid="input-buyer-name" /></div>
              <div><Label>Your Email *</Label><Input name="buyer_email" value={form.buyer_email} onChange={handleChange} placeholder="your@email.com" type="email" className="mt-1" data-testid="input-buyer-email" /></div>
              <hr className="my-4" />
              <div><Label>Recipient's Name *</Label><Input name="recipient_name" value={form.recipient_name} onChange={handleChange} placeholder="Their full name" className="mt-1" data-testid="input-recipient-name" /></div>
              <div><Label>Recipient's Email *</Label><Input name="recipient_email" value={form.recipient_email} onChange={handleChange} placeholder="their@email.com" type="email" className="mt-1" data-testid="input-recipient-email" /></div>
            </div>
            <Button onClick={() => setStep(1)} className="w-full mt-6 btn-primary text-white py-5 rounded-full border-0 font-semibold" data-testid="next-to-message">
              Continue <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 1: Personal Message */}
        {step === 1 && (
          <div className="bg-white rounded-2xl shadow-lg p-8 fade-in" data-testid="step-message">
            <h3 className="text-xl font-bold text-gray-800 mb-2">Add a Personal Message</h3>
            <p className="text-sm text-gray-500 mb-6">This will be included in the email to {form.recipient_name || 'the recipient'}.</p>
            <Textarea
              name="message"
              value={form.message}
              onChange={handleChange}
              placeholder="Dear Mum, I thought it would be wonderful to capture your life story..."
              rows={5}
              className="w-full"
              data-testid="input-message"
            />
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setStep(0)} className="flex-1 py-5 rounded-full">Back</Button>
              <Button onClick={() => setStep(2)} className="flex-1 btn-primary text-white py-5 rounded-full border-0 font-semibold" data-testid="next-to-payment">
                Continue <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Payment Confirmation */}
        {step === 2 && (
          <div className="bg-white rounded-2xl shadow-lg p-8 fade-in" data-testid="step-payment">
            <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2"><CreditCard className="w-5 h-5 text-purple-500" /> Confirm Gift</h3>
            <div className="space-y-3 mb-6">
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm text-gray-500">From</p>
                <p className="font-semibold text-gray-800">{form.buyer_name}</p>
                <p className="text-sm text-gray-600">{form.buyer_email}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm text-gray-500">Gift for</p>
                <p className="font-semibold text-gray-800">{form.recipient_name}</p>
                <p className="text-sm text-gray-600">{form.recipient_email}</p>
              </div>
              {form.message && (
                <div className="bg-pink-50 rounded-xl p-4">
                  <p className="text-sm text-gray-500">Your message</p>
                  <p className="text-sm text-gray-700 italic">"{form.message}"</p>
                </div>
              )}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4">
                <div className="flex justify-between font-bold text-lg text-gray-800"><span>Total</span><span>£25</span></div>
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1 py-5 rounded-full">Back</Button>
              <Button onClick={handleCheckout} disabled={processing} className="flex-1 btn-primary text-white py-5 rounded-full border-0 font-semibold" data-testid="pay-gift-btn">
                {processing ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto" /> : <><Gift className="w-4 h-4 mr-2" /> Pay £25 & Send Gift</>}
              </Button>
            </div>
            <p className="text-center text-xs text-gray-400 mt-4">After payment, {form.recipient_name} will receive an email with a link to create their autobiography.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GiftPurchase;
